/**
 * Maps one row of `GET /referral-service/admin/referrals` onto the shape the
 * existing Referral table columns read (`jobs-column.js`'s `referralColumns`,
 * `ReferralList`'s `Table`, `ActionComponent`).
 *
 * Wire shape confirmed against referral-service's own response types
 * (`http/api/admin/referrals/response.go`, `list.go` on
 * feat-GOTU-7170-admin-read-endpoint): each row is `{ id, program,
 * migratedFrom, code, status, isCancelled, cancelledAt, amountCents,
 * rateCardId, referrer:{id,type,firstName,lastName,officeName,email,
 * phoneNumber}, receiver:{same}, attributedAt, payoutRequestedAt, paidAt }`.
 * `amountCents` and `attributedAt` are top-level on the referral, not nested
 * under either party, and the parties are named `referrer`/`receiver`, not
 * `sender`/`receiver` — an earlier draft of this file assumed a `sender`
 * field and per-party amounts before the endpoint existed to check against;
 * both were wrong.
 *
 * `referrer`/`receiver` carry a `type` of `professional`/`office`, which the
 * columns need back as the monolith's `HYG`/`DNT` — the rest of the app (row
 * links, `UserType`, `ActionComponent`'s DNT/DNT cancel check) is written
 * against those two literals and there is no value in threading a third
 * vocabulary through it.
 *
 * The service prices in cents and only ever carries the referrer's amount —
 * the Receiver Amount column referral-service does not reproduce is dropped
 * in `jobs-column.js`, not patched over here.
 */

const REFERRER_TYPE_TO_USER_TYPE = {
  professional: 'HYG',
  office: 'DNT',
};

const toUserType = (type) => REFERRER_TYPE_TO_USER_TYPE[type] ?? type;

// A party whose projection has not arrived (or was tombstoned) comes back with
// null names; without a fallback the columns render "null null" or an empty,
// unclickable link.
const unknownParty = (party) => `Unknown (${party.id})`;

const mapParty = (party = {}) => {
  const isNamelessPerson = party.type === 'professional' && !party.firstName && !party.lastName;
  return {
    id: party.id,
    user_type: toUserType(party.type),
    first_name: isNamelessPerson ? unknownParty(party) : party.firstName,
    last_name: isNamelessPerson ? '' : party.lastName,
    office_name:
      party.type === 'office' && !party.officeName ? unknownParty(party) : party.officeName,
  };
};

const toDollars = (cents) => (cents === null || cents === undefined ? null : cents / 100);

/** One `<Referral>` -> the row shape `referralColumns` and `ActionComponent` read. */
export const mapReferral = (referral = {}) => ({
  id: referral.id,
  migratedFrom: referral.migratedFrom ?? null,
  program: referral.program,
  status: referral.status,
  isCancelled: Boolean(referral.isCancelled),
  cancelledAt: referral.cancelledAt ?? null,
  paidOutTime: referral.paidAt ?? null,
  sender: {
    ...mapParty(referral.referrer),
    amount: toDollars(referral.amountCents),
  },
  receiver: {
    ...mapParty(referral.receiver),
    created_at: referral.attributedAt ?? null,
  },
});

export const mapReferrals = (referrals = []) => referrals.map(mapReferral);
